import { useCallback, useEffect, useRef, useState } from "react";

import TextInput from "../../../shared/ui/Field/TextInput";
import Spinner from "../../../shared/ui/Loading/Spinner";
import { messageFromError } from "../../../shared/api/drfErrors";
import { fetchProperties as fetchPersonalProperties } from "../../realtyList/api/realty";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import { fetchCompanyProperties } from "../api/companyProperties";
import {
  propertyDisplayTitle,
  propertyStatusLabel,
  propertyTypeLabel,
} from "../lib/propertyLabels";

export const PROPERTY_SCOPE_PERSONAL = "personal";
export const PROPERTY_SCOPE_COMPANY = "company";

function unwrapResults(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

/**
 * Scope-aware Property loader/picker.
 * Personal and company inventories never merge into an unlabeled array.
 */
function ScopeAwarePropertyPicker({
  scope = PROPERTY_SCOPE_PERSONAL,
  companyId = null,
  actorId = null,
  membershipGeneration = null,
  value = null,
  onChange,
  disabled = false,
  label = "Об'єкт нерухомості",
  searchPlaceholder = "Пошук за назвою…",
  searchDescribedBy,
  searchInvalid = false,
}) {
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const requestGenRef = useRef(0);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);

  const clearSelectionIfNeeded = useCallback(() => {
    if (value != null && typeof onChange === "function") {
      onChange(null);
    }
  }, [onChange, value]);

  const load = useCallback(async () => {
    const generation = ++requestGenRef.current;
    const op = scopeGate.beginRead({
      companyId:
        scope === PROPERTY_SCOPE_COMPANY ? companyId : `personal:${actorId}`,
      actorId,
      membershipGeneration,
      resourceId: scope,
    });
    setLoadState("loading");
    setLoadError(null);
    try {
      let data;
      if (scope === PROPERTY_SCOPE_COMPANY) {
        if (!companyId) {
          setOptions([]);
          setLoadState("ready");
          return;
        }
        data = await fetchCompanyProperties(companyId, {
          search: query.trim() || undefined,
          page_size: 20,
        });
      } else {
        data = await fetchPersonalProperties({
          search: query.trim() || undefined,
          page_size: 20,
        });
      }
      if (
        generation !== requestGenRef.current ||
        !scopeGate.readStillValid(op, { mounted: mountedRef.current })
      ) {
        return;
      }
      setOptions(unwrapResults(data));
      setLoadState("ready");
    } catch (err) {
      if (
        generation !== requestGenRef.current ||
        !scopeGate.readStillValid(op, { mounted: mountedRef.current })
      ) {
        return;
      }
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }
      setOptions([]);
      setLoadError(messageFromError(err));
      setLoadState("error");
    }
  }, [actorId, companyId, membershipGeneration, query, scope, scopeGate]);

  const hadPriorIdentityRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({
      companyId:
        scope === PROPERTY_SCOPE_COMPANY ? companyId : `personal:${actorId}`,
      actorId,
      membershipGeneration,
      bindMembershipGeneration: membershipGeneration != null,
    });
    setOptions([]);
    // Clear stale selection only after a real identity change — not on first
    // mount, so authorized parent prefills survive picker bind/load.
    if (hadPriorIdentityRef.current) {
      clearSelectionIfNeeded();
    }
    hadPriorIdentityRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [scope, companyId, actorId, membershipGeneration]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopeHint =
    scope === PROPERTY_SCOPE_COMPANY
      ? "Об'єкти цієї компанії"
      : "Особисті об'єкти";
  const emptyHint =
    scope === PROPERTY_SCOPE_COMPANY
      ? "Об'єктів компанії не знайдено."
      : "Особистих об'єктів не знайдено.";
  const scopeOptionLabel =
    scope === PROPERTY_SCOPE_COMPANY ? "Компанія" : "Особисті";

  return (
    <div
      className="feature-company-properties__scope-picker"
      data-scope={scope}
    >
      <div className="feature-client-detail__property-search">
        <TextInput
          aria-label={label}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          disabled={disabled}
          aria-describedby={searchDescribedBy}
          aria-invalid={searchInvalid ? "true" : undefined}
        />
        <button
          type="button"
          className="ui-form__button ui-form__button--ghost"
          onClick={load}
          disabled={disabled || loadState === "loading"}
        >
          {loadState === "loading" ? (
            <Spinner size="sm" label="Пошук" />
          ) : (
            "Шукати"
          )}
        </button>
      </div>
      <p className="feature-company-workspace__meta" aria-live="polite">
        {scopeHint}
      </p>
      {loadError ? (
        <p className="feature-company-workspace__error" role="alert">
          {loadError}
        </p>
      ) : null}
      {loadState === "loading" && options.length === 0 ? (
        <div className="feature-client-detail__property-picker-state">
          <Spinner size="sm" label="Завантаження об'єктів" />
        </div>
      ) : null}
      {loadState !== "loading" && options.length === 0 ? (
        <p className="feature-company-workspace__meta">
          {emptyHint}
        </p>
      ) : (
        <ul className="feature-client-detail__property-options" role="listbox" aria-label={label}>
          {options.map((item) => {
            const selected =
              value != null && String(value) === String(item.id);
            return (
              <li key={`${scope}-${item.id}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    "feature-client-detail__property-option",
                    selected
                      ? "feature-client-detail__property-option--selected"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  disabled={disabled}
                  onClick={() => {
                    if (typeof onChange === "function") {
                      onChange(item.id, {
                        ...item,
                        __scope: scope,
                        __companyId:
                          scope === PROPERTY_SCOPE_COMPANY ? companyId : null,
                      });
                    }
                  }}
                >
                  <span>
                    {propertyDisplayTitle(item)} · {scopeOptionLabel}
                  </span>
                  <span className="feature-company-workspace__meta">
                    {propertyTypeLabel(item.property_type)} ·{" "}
                    {propertyStatusLabel(item.status)}
                    {item?.location?.settlement_name
                      ? ` · ${item.location.settlement_name}`
                      : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function scopesCompatible(a, b) {
  return (
    a?.scope === b?.scope &&
    sameActorId(a?.actorId, b?.actorId) &&
    (a?.scope !== PROPERTY_SCOPE_COMPANY ||
      sameCompanyId(a?.companyId, b?.companyId))
  );
}

export default ScopeAwarePropertyPicker;
